import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CheckCircle, Sparkle, ShieldCheck, ChatCircleDots, Clock, Confetti } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";

const PERKS = [
  "7-day free trial · cancel anytime",
  "Demo project auto-seeded (BOQ + monthly entries)",
  "52 EPDs · 51 countries · 29 frameworks",
  "AI Carbon Copilot powered by Claude Sonnet 4.5",
  "TOTP 2FA · enterprise-grade security",
];

const LIVE_TICKER = [
  "+$2,418 carbon savings unlocked",
  "1 procurement deadline closes in 3 days",
  "CBAM exposure dropped 12% this week",
  "EN15978 A1-A5 ready",
  "Net-Zero 2050 trajectory on track",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tickerIdx, setTickerIdx] = useState(0);

  // Rotate the live ticker
  useEffect(() => {
    const id = setInterval(() => setTickerIdx((i) => (i + 1) % LIVE_TICKER.length), 3000);
    return () => clearInterval(id);
  }, []);

  const pwScore = (() => {
    let s = 0;
    if (password.length >= 6) s++;
    if (password.length >= 10) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^a-zA-Z0-9]/.test(password)) s++;
    return s; // 0..5
  })();
  const pwLabel = ["Too short", "Weak", "Fair", "Good", "Strong", "Excellent"][pwScore];

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success("Workspace created. 7-day trial activated.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative bg-[#0F1115] text-white overflow-hidden">
      {/* animated gradient background */}
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 40% 50% at 12% 30%, rgba(212,175,55,0.18), transparent 60%)," +
            "radial-gradient(ellipse 40% 50% at 90% 80%, rgba(74,222,128,0.12), transparent 60%)," +
            "radial-gradient(ellipse 60% 40% at 60% 0%, rgba(56,189,248,0.10), transparent 70%)",
        }}
      />
      <div className="absolute inset-0 bg-grid-fine opacity-30 pointer-events-none" />

      <div className="relative grid lg:grid-cols-[1.1fr_1fr] min-h-screen">
        {/* Left: editorial / sales */}
        <div className="hidden lg:flex flex-col justify-between p-12 xl:p-16">
          <div>
            <div className="overline">GPI</div>
            <div className="font-serif-display text-5xl mt-2 leading-none">CarbonIQ</div>
          </div>

          <div className="max-w-xl space-y-8">
            <div className="inline-flex items-center gap-2 border border-gold/30 bg-gold/5 px-4 py-1.5 rounded-full text-xs">
              <Clock size={12} weight="fill" className="text-gold animate-pulse-gold" />
              <span className="text-gold tracking-wider">7-DAY FREE TRIAL · NO CARD REQUIRED</span>
            </div>

            <h1 className="font-serif-display text-5xl xl:text-7xl leading-[0.95]">
              Start measuring<br />
              <span className="gradient-text-gold">what gets locked in.</span>
            </h1>

            <p className="text-ink-muted text-lg leading-relaxed">
              Embodied carbon, operational tracking, procurement lock-in, and an AI Copilot — in a single workspace.
              Your 7-day trial begins the moment you click &quot;Create&quot;.
            </p>

            <div className="space-y-2">
              {PERKS.map((p, i) => (
                <div
                  key={p}
                  className="flex items-center gap-3 text-sm animate-slide-up"
                  style={{ animationDelay: `${i * 90}ms` }}
                >
                  <CheckCircle size={16} weight="fill" className="text-eco shrink-0" />
                  <span className="text-white">{p}</span>
                </div>
              ))}
            </div>

            {/* Live ticker pill */}
            <div className="mt-8 border border-white/10 bg-black/30 backdrop-blur-sm px-4 py-3 rounded-sm">
              <div className="overline mb-1 flex items-center gap-2"><Sparkle size={10} weight="fill" className="text-gold" /> Live across the platform right now</div>
              <div className="font-mono-data text-sm text-white min-h-[20px]" key={tickerIdx}>{LIVE_TICKER[tickerIdx]}</div>
            </div>
          </div>

          <div className="flex items-center gap-6 text-[11px] font-mono-data text-ink-dim tracking-widest">
            <span className="flex items-center gap-1.5"><ShieldCheck size={11} weight="fill" /> TOTP 2FA</span>
            <span className="flex items-center gap-1.5"><ChatCircleDots size={11} weight="fill" /> CLAUDE SONNET 4.5</span>
            <span>RAZORPAY USD</span>
            <span>TLS 1.2+</span>
          </div>
        </div>

        {/* Right: form panel */}
        <div className="relative flex items-center justify-center p-8">
          <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-gold/40 to-transparent hidden lg:block" />
          <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="register-form">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Confetti size={20} weight="fill" className="text-gold" />
                <div className="overline">Create account</div>
              </div>
              <h2 className="font-serif-display text-4xl leading-tight">Start your free trial.</h2>
              <p className="text-sm text-ink-muted mt-2">No credit card. Full platform access for 7 days.</p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="overline block mb-1.5">Full name</label>
                <input
                  data-testid="register-name-input"
                  value={name} onChange={(e) => setName(e.target.value)} required
                  className="w-full bg-[#1A1D24] border border-white/10 px-3 py-2.5 text-sm text-white rounded-sm focus:outline-none focus:border-gold transition-colors"
                  placeholder="Jane Architect"
                />
              </div>
              <div>
                <label className="overline block mb-1.5">Work email</label>
                <input
                  data-testid="register-email-input"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full bg-[#1A1D24] border border-white/10 px-3 py-2.5 text-sm text-white rounded-sm focus:outline-none focus:border-gold transition-colors"
                  placeholder="you@firm.com"
                />
              </div>
              <div>
                <label className="overline flex items-center justify-between mb-1.5">
                  <span>Password</span>
                  {password && (
                    <span className={`text-[10px] ${pwScore >= 4 ? "text-eco" : pwScore >= 2 ? "text-warn" : "text-risk"}`}>
                      {pwLabel}
                    </span>
                  )}
                </label>
                <input
                  data-testid="register-password-input"
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  className="w-full bg-[#1A1D24] border border-white/10 px-3 py-2.5 text-sm text-white rounded-sm focus:outline-none focus:border-gold transition-colors"
                  placeholder="At least 6 characters" />
                {password && (
                  <div className="flex gap-1 mt-1.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <span key={i} className={`h-[3px] flex-1 ${i <= pwScore ? (pwScore >= 4 ? "bg-eco" : pwScore >= 2 ? "bg-warn" : "bg-risk") : "bg-white/[0.06]"}`} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div data-testid="register-error" className="text-sm text-risk border border-risk/30 bg-risk/10 px-3 py-2">{error}</div>
            )}

            <button
              type="submit" data-testid="register-submit-btn" disabled={busy}
              className="w-full bg-gold text-black font-medium py-3 rounded-sm hover:bg-[#e0bc40] disabled:opacity-50 flex items-center justify-center gap-2 glow-gold"
            >
              {busy ? "Creating workspace…" : <>Start 7-day trial <ArrowRight size={16} weight="bold" /></>}
            </button>

            <div className="text-xs text-ink-muted text-center">
              Already on CarbonIQ?{" "}
              <Link to="/login" className="text-gold hover:underline" data-testid="goto-login-link">Sign in</Link>
            </div>

            <div className="text-[10px] text-ink-dim text-center leading-relaxed">
              By creating an account you accept our{" "}
              <a href="https://gpi.ai/terms" className="underline" target="_blank" rel="noreferrer">Terms</a>{" "}
              and{" "}
              <a href="https://gpi.ai/privacy" className="underline" target="_blank" rel="noreferrer">Privacy Policy</a>.
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
